/**
 * Social listening (RND-2900): trackers collect public posts through a
 * provider (Apify) into the Knowledge Hub. A sync writes its new posts as one
 * digest document through the canonical Hub ingest; each post is one `post`
 * span in that digest, so citations resolve to a single post.
 */

export const SOCIAL_PLATFORMS = [
  "reddit",
  "youtube",
  "tiktok",
  "instagram",
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const SOCIAL_PROVIDERS = ["apify"] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

export const SOCIAL_TRACKER_STATUSES = [
  "queued",
  "collecting",
  "indexing",
  "ready",
  "partial",
  "failed",
  "rate_limited",
  "over_budget",
  "paused",
] as const;
/** Persisted tracker status. `stale` is derived, never stored. */
export type SocialTrackerStatus = (typeof SOCIAL_TRACKER_STATUSES)[number];

export const SOCIAL_SYNC_RUN_STATUSES = [
  "queued",
  "running",
  "indexing",
  "succeeded",
  "partial",
  "failed",
  "rate_limited",
  "over_budget",
] as const;
export type SocialSyncRunStatus = (typeof SOCIAL_SYNC_RUN_STATUSES)[number];

/** Heading the digest writer emits per post; segmentation splits on it. */
export const SOCIAL_DIGEST_POST_HEADING_PREFIX = "## [post:";

/** v1 fixed settings (PRD section 8). */
/** Orders of a tracker's post list: newest first, or most comments or likes first. */
export const SOCIAL_POSTS_SORTS = ["newest", "comments", "likes"] as const;
export type SocialPostsSort = (typeof SOCIAL_POSTS_SORTS)[number];

export const SOCIAL_FIRST_IMPORT_DAYS = 30;
export const SOCIAL_SYNC_CADENCE_HOURS = 24;
/** A tracker is stale when its last success is older than 2x the cadence. */
export const SOCIAL_STALE_AFTER_HOURS = SOCIAL_SYNC_CADENCE_HOURS * 2;
export const SOCIAL_DEFAULT_RETENTION_DAYS = 90;
export const SOCIAL_MAX_POSTS_PER_RUN = 500;
export const SOCIAL_ALERT_AFTER_CONSECUTIVE_FAILURES = 2;

export interface SocialTrackerInput {
  name: string;
  platform: SocialPlatform;
  /** One or more search terms; matched by the provider, never by us. */
  keywords: Array<string>;
  /** Optional accounts (subreddits / channels / profiles) to collect from. */
  handles?: Array<string>;
  /** BCP-47 language, e.g. "en", "fr". */
  language: string;
  /** Market label, e.g. "France". */
  market: string;
  /** Library labels put on every digest document the tracker writes. */
  labels?: Array<SocialTrackerLabel>;
  /** What counts as relevant; every new post is judged against it. */
  brief?: SocialTrackerBrief;
  /** What each keyword means; a note for a keyword not in `keywords` is dropped. */
  keywordNotes?: Array<SocialKeywordNote>;
  /** Tags put on relevant posts; send an existing topic's id to keep it. */
  topics?: Array<SocialTopicInput>;
}

/**
 * The tracker's relevance brief. A brand tracker keeps posts about the brand
 * only; `include` and `exclude` say what that covers in the client's words.
 */
export interface SocialTrackerBrief {
  include: string;
  exclude: string;
}

export interface SocialKeywordNote {
  keyword: string;
  description: string;
}

/** A per-tracker tag on posts. The id is the server's and survives a rename. */
export interface SocialTopic {
  id: string;
  name: string;
  description: string;
}

/** A topic as a client sends it: without an id when it is new. */
export interface SocialTopicInput {
  id?: string;
  name: string;
  description: string;
}

export const SOCIAL_MAX_TOPICS = 12;
export const SOCIAL_BRIEF_MAX_LENGTH = 1000;
export const SOCIAL_NOTE_MAX_LENGTH = 300;
export const SOCIAL_TOPIC_NAME_MAX_LENGTH = 60;
/** Off-topic posts a run keeps as `{url, reason}` so the client can check the filter. */
export const SOCIAL_OFF_TOPIC_SAMPLES_PER_RUN = 10;

/** Facet kinds a client can label a tracker's Library documents with. */
export const SOCIAL_TRACKER_LABEL_KINDS = ["brand", "market", "category"] as const;
export type SocialTrackerLabelKind = (typeof SOCIAL_TRACKER_LABEL_KINDS)[number];

export interface SocialTrackerLabel {
  kind: SocialTrackerLabelKind;
  value: string;
}

/**
 * A tracker suggested from the org's context, by the Ask agent
 * (`propose_social_tracker`) or by Library's "Suggest with AI". Nothing is
 * created until the client confirms it.
 */
export interface SocialTrackerProposal {
  name: string;
  platform: SocialPlatform;
  keywords: Array<string>;
  handles: Array<string>;
  language: string;
  market: string;
  labels: Array<SocialTrackerLabel>;
  /** One plain sentence on what the tracker would add. */
  rationale: string;
  /** The drafted relevance brief; the three below are absent on proposals saved before them. */
  brief?: SocialTrackerBrief;
  keywordNotes?: Array<SocialKeywordNote>;
  /** Drafted topics; the server gives them ids when the tracker is created. */
  topics?: Array<SocialTopicInput>;
  /** Set once the client created a tracker from this proposal. */
  createdTrackerToken: string | null;
}

export interface SocialTrackerSuggestionRequest {
  /** What the client wants to learn; optional. */
  goal?: string;
  /** Explicit search terms and meanings from the Topics step; returned unchanged. */
  seed?: {
    keywords: Array<string>;
    keywordNotes: Array<SocialKeywordNote>;
  };
  /** The platform the client already picked; the model picks one when absent. */
  platform?: SocialPlatform;
}

export interface SocialTrackerItem {
  token: string;
  name: string;
  provider: SocialProvider;
  platform: SocialPlatform;
  keywords: Array<string>;
  handles: Array<string>;
  language: string;
  market: string;
  labels: Array<SocialTrackerLabel>;
  brief: SocialTrackerBrief;
  keywordNotes: Array<SocialKeywordNote>;
  topics: Array<SocialTopic>;
  status: SocialTrackerStatus;
  /** Derived: last success older than SOCIAL_STALE_AFTER_HOURS. */
  stale: boolean;
  lastError: string | null;
  lastSyncedAt: string | null;
  newestPostAt: string | null;
  postsLast7Days: number;
  /** Collected posts published in the past 30 days; absent on older servers. */
  postsLast30Days?: number;
  totalPosts: number;
  consecutiveFailures: number;
  /** New posts in the latest finished run; 0 renders "no new posts today". */
  lastRunNewPosts?: number | null;
  /** Knowledge collection that scopes "Ask about this tracker". */
  collectionToken: string | null;
  retentionDays: number;
  createdAt: string;
}

export interface SocialTrackersResponse {
  /** False when SOCIAL_LISTENING_ENABLED is off: the UI hides the tab. */
  enabled: boolean;
  /** OWNER or ADMIN: may add, edit, pause, delete and remove posts. */
  canManage: boolean;
  trackers: Array<SocialTrackerItem>;
}

export interface SocialTrackerUpdate {
  name?: string;
  keywords?: Array<string>;
  handles?: Array<string>;
  language?: string;
  market?: string;
  labels?: Array<SocialTrackerLabel>;
  brief?: SocialTrackerBrief;
  keywordNotes?: Array<SocialKeywordNote>;
  topics?: Array<SocialTopicInput>;
}

export interface SocialSyncRunsResponse {
  runs: Array<SocialSyncRunItem>;
}

/**
 * HTTP contract (all org-scoped, JWT cookie):
 *   GET    /social-listening/trackers                      -> SocialTrackersResponse
 *   POST   /social-listening/trackers          (manage)    SocialTrackerInput -> SocialTrackerItem
 *   PATCH  /social-listening/trackers/:token   (manage)    SocialTrackerUpdate -> SocialTrackerItem
 *   POST   /social-listening/suggestions       (manage)    SocialTrackerSuggestionRequest -> SocialTrackerProposal
 *   POST   /social-listening/trackers/:token/sync   (manage) -> SocialTrackerItem   (run now / retry)
 *   POST   /social-listening/trackers/:token/pause  (manage) -> SocialTrackerItem
 *   POST   /social-listening/trackers/:token/resume (manage) -> SocialTrackerItem
 *   DELETE /social-listening/trackers/:token   (manage)    hard delete -> 204
 *   GET    /social-listening/trackers/:token/posts?cursor=&limit= -> SocialPostsResponse
 *   GET    /social-listening/trackers/:token/runs          -> SocialSyncRunsResponse
 *   DELETE /social-listening/posts/:token      (manage)    remove one post -> 204
 *   DELETE /social-listening/trackers/:token/authors/:handle (manage) remove an author's posts -> 204
 *   GET    /knowledge/documents?source=docs|social  (default docs: digests excluded)
 *   Ask bodies accept `sourceKinds?: Array<KnowledgeSourceKind>` and `socialWindow?: SocialWindow`.
 */

export interface SocialPostEngagement {
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
  views?: number | null;
}

export interface SocialPostItem {
  token: string;
  platform: SocialPlatform;
  url: string;
  authorHandle: string | null;
  publishedAt: string | null;
  collectedAt: string;
  language: string | null;
  text: string;
  engagement: SocialPostEngagement;
  /** The platform's official embed of the post, loaded on demand; null when the platform has none. */
  embedUrl: string | null;
  media: SocialPostMedia;
  /** Ids of the tracker's topics the post was tagged with. */
  topicIds: Array<string>;
}

/**
 * Provider media links as collected. Instagram and TikTok sign them, so a
 * thumbnail stops loading a few days after collection; the embed does not.
 */
export interface SocialPostMedia {
  thumbnailUrl: string | null;
  imageCount: number;
  hasVideo: boolean;
}

export interface SocialPostsResponse {
  posts: Array<SocialPostItem>;
  nextCursor: string | null;
}

export interface SocialSyncRunItem {
  token: string;
  status: SocialSyncRunStatus;
  itemsFetched: number;
  itemsNew: number;
  /** Back-office test limit this run was capped at (local and dev only). */
  postLimit: number | null;
  /** New posts judged not about the tracker's subject and deleted. */
  itemsOffTopic: number;
  /** New posts dropped because the platform reported another language. */
  itemsLanguageSkipped: number;
  offTopicSamples: Array<SocialOffTopicSample>;
  costUsd: number | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface SocialOffTopicSample {
  url: string;
  reason: string;
}

/** Default and bounds of the local/dev back-office post limit per sync. */
export const SOCIAL_TEST_POST_LIMIT_DEFAULT = 50;
export const SOCIAL_TEST_POST_LIMIT_MIN = 1;

/** The local/dev post limit per sync, as the back office reads and writes it. */
export interface SocialTestPostLimit {
  enabled: boolean;
  maxPosts: number;
  /** DIGITAL_TWINS_ENVIRONMENT of the backend serving it, e.g. `dev`. */
  environment: string;
}

export interface UpdateSocialTestPostLimitRequest {
  enabled: boolean;
  maxPosts: number;
}

/** Post metadata attached to a citation whose granularity is `post`. */
export interface SocialCitationPost {
  postToken: string;
  platform: SocialPlatform;
  url: string;
  authorHandle: string | null;
  publishedAt: string | null;
  trackerName: string | null;
  /** The four fields below are absent on answers saved before they existed. */
  text?: string;
  engagement?: SocialPostEngagement;
  embedUrl?: string | null;
  /** Signed provider link; see `SocialPostMedia`. */
  thumbnailUrl?: string | null;
}

/** The platform's official embed of a stored post; null when it has none. */
export function socialPostEmbedUrl(post: {
  platform: SocialPlatform;
  platformPostId: string;
  url: string;
}): string | null {
  const id = encodeURIComponent(post.platformPostId);
  switch (post.platform) {
    case "youtube":
      return `https://www.youtube-nocookie.com/embed/${id}`;
    case "tiktok":
      return `https://www.tiktok.com/player/v1/${id}`;
    case "instagram": {
      const permalink = new URL(post.url);
      return `${permalink.origin}${permalink.pathname.replace(/\/$/, "")}/embed/`;
    }
    case "reddit": {
      const permalink = new URL(post.url);
      return /(^|\.)reddit\.com$/.test(permalink.hostname)
        ? `https://embed.reddit.com${permalink.pathname}?embed=true`
        : null;
    }
  }
}

/** Ask scope additions: restrict an ask to library documents or social digests. */
export const KNOWLEDGE_SOURCE_KINDS = ["docs", "social"] as const;
export type KnowledgeSourceKind = (typeof KNOWLEDGE_SOURCE_KINDS)[number];

export interface SocialWindow {
  /** ISO date-time, inclusive. */
  from: string;
  /** ISO date-time, inclusive. */
  to: string;
}

/** Longest `socialWindow` an ask accepts. */
export const SOCIAL_ASK_WINDOW_MAX_DAYS = 366;

/**
 * Source scope carried by an ask body and a thread scope. `sourceKinds`
 * `["social"]` keeps only social digests, `["docs"]` excludes them; both or
 * absent leaves the library unrestricted. `socialWindow` keeps the digests
 * holding at least one post published inside it, and implies `social`.
 */
export interface KnowledgeAskSourceScope {
  sourceKinds?: Array<KnowledgeSourceKind>;
  socialWindow?: SocialWindow | null;
}
